import { computed } from 'mobx';
import merge from 'deepmerge';

import UserStore from 'stores/UserStore';
import GamesStore from 'stores/GamesStore';
import { GAMES } from 'utils/consts';
import { ThemeFlagType, ControlledFeatureType, Theme } from 'themes/themes.types';

import { getCampaignWithFlag, removeEmpty } from './utils';
import DefaultTheme from './default/DefaultTheme';
import Overrides from './overrides/Overrides';
import AugustTheme from './august/AugustTheme';
import BackToCollegeTheme from './backToCollege/BackToCollegeTheme';
import BirthdayTheme from './birthday/BirthdayTheme';
import BlackFridayTheme from './blackFriday/BlackFridayTheme';
import BlueBallsTheme from './blueBalls/BlueBallsTheme';
import BonusTokensTheme from './bonusTokens/BonusTokensTheme';
import CincoDeMayoTheme from './cincoDeMayo/CincoDeMayoTheme';
import DayOfferTheme from './dayOffer/DayOfferTheme';
import HalloweenTheme from './halloween/HalloweenTheme';
import HappyHourTheme from './happyHour/HappyHourTheme';
import NewYearTheme from './newYear/NewYearTheme';
import SecondPurchaseTheme from './secondPurchase/SecondPurchaseTheme';
import SpringTheme from './spring/SpringTheme';
import StPatricksTheme from './stPatricks/StPatricksTheme';
import SummerTheme from './summer/SummerTheme';
import ValentinesTheme from './valentines/ValentinesTheme';
import XmasTheme from './xmas/XmasTheme';


const THEMES = [
  AugustTheme,
  BackToCollegeTheme,
  BirthdayTheme,
  BlackFridayTheme,
  BlueBallsTheme,
  BonusTokensTheme,
  CincoDeMayoTheme,
  DayOfferTheme,
  HalloweenTheme,
  HappyHourTheme,
  NewYearTheme,
  SecondPurchaseTheme,
  SpringTheme,
  StPatricksTheme,
  SummerTheme,
  ValentinesTheme,
  XmasTheme,
];

const GAMES_MAP = {
  [GAMES.ROLL_THE_DICE]: 'dice',
  [GAMES.SPIN_THE_WHEEL]: 'wheel',
  [GAMES.TOKEN_SQUARES]: 'keno',
};

const themeInstances = new Map();

const getThemeInstance = (ThemeClass: any) => {
  if (!themeInstances.has(ThemeClass)) {
    themeInstances.set(ThemeClass, new ThemeClass());
  }

  return themeInstances.get(ThemeClass);
};

export class ThemeService {
  themes: (typeof AugustTheme)[];

  constructor(themes: (typeof AugustTheme)[] = THEMES) {
    this.themes = themes;
  }

  /**
   * Get theme object for the theme that has certain Controlled Feature active for current user
   * @param controlledFeatureType Type (key) if the Controlled Feature
   * @returns Theme object
   */
  getThemeForCF = (controlledFeatureType: ControlledFeatureType) => {
    const ThemeClass = this.themes.find((CurrentThemeClass) => CurrentThemeClass.CFS[controlledFeatureType] &&
      UserStore.hasCF(CurrentThemeClass.CFS[controlledFeatureType])
    );
    if (ThemeClass) {
      return getThemeInstance(ThemeClass).theme;
    }

    return {};
  };

  /**
   * Get theme object for the theme that has the Theme Flag in the CampaignDetails of one of current active campaigns.
   * If several campaigns have flags for this particular theme part, the one with higher priority will be returned.
   * @param themeFlag Type (key) of the Theme Flag
   * @returns Theme object
   */
  getThemeForThemeFlag = (themeFlagType: ThemeFlagType) => {
    const ThemeClass = this.themes
      // Get all the themes that have flag of this type among the active campaigns
      .reduce((result, CurrentThemeClass) => {
        const themeFlagName = CurrentThemeClass.CAMPAIGN_FLAGS[themeFlagType];
        const campaign = themeFlagName && getCampaignWithFlag(themeFlagName);
        if (campaign) {
          result.push({
            CurrentThemeClass,
            priority: campaign.priority,
          });
        }

        return result;
      }, [] as { CurrentThemeClass: typeof HalloweenTheme, priority: number }[])
      // Sort them by corresponding campaign's priorities
      .sort((item1, item2) => item2.priority - item1.priority)
      // And take the one with the highest priority
      .map(({ CurrentThemeClass }) => CurrentThemeClass)[0];
    if (ThemeClass) {
      return getThemeInstance(ThemeClass).theme;
    }

    return {};
  };

  /** Construct theme object from the current active theme merged upon the default theme */
  @computed({ keepAlive: true }) get theme(): Theme {
    const currentTheme = removeEmpty({
      games: this.getThemeForThemeFlag('GAMES').games || this.getThemeForCF('GAMES').games,
      logoImage: this.getThemeForThemeFlag('LOGO').logoImage || this.getThemeForCF('LOGO').logoImage,
      boost: this.getThemeForThemeFlag('BOOST').boost || this.getThemeForCF('BOOST').boost,
      tipping: this.getThemeForThemeFlag('TIPPING').tipping || this.getThemeForCF('TIPPING').tipping,
      onCamAnimation: this.getThemeForThemeFlag('ON_CAM_ANIMATION').onCamAnimation,
      buyTokensModal: this.getThemeForThemeFlag('BUY_TOKENS_MODAL').buyTokensModal,
      headerCTA: this.getThemeForThemeFlag('HEADER_CTA').headerCTA,
      mobileOverlay: this.getThemeForThemeFlag('MOBILE_OVERLAY').mobileOverlay,
      chatOffer: this.getThemeForThemeFlag('CHAT_OFFER').chatOffer,
      infoStrip: this.getThemeForThemeFlag('INFO_STRIP').infoStrip,
      registrationOverlay: this.getThemeForThemeFlag('REGISTRATION_OVERLAY').registrationOverlay,
      drawerMenu: this.getThemeForThemeFlag('DRAWER_MENU').drawerMenu,
    });

    const defaultTheme = DefaultTheme.theme;
    const overrides = new Overrides().theme;

    return merge.all([defaultTheme, currentTheme, overrides]) as Theme;
  }

  /** Returns theme object for the current active game (for the current stream) */
  @computed get currentGameTheme() {
    const { activeGameName } = GamesStore;
    const gameObjectId = GAMES_MAP[activeGameName];
    if (gameObjectId === 'dice' || gameObjectId === 'wheel' || gameObjectId === 'keno') {
      return this.theme.games[gameObjectId];
    }

    return {};
  }
}

export default new ThemeService();
